import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Snow from '../../../utils/snow';
import { useAppTheme } from '../../../themes/useAppTheme';
import { getEasterTheme } from '../../../themes/easterTheme';

const supportAgentSvg =
    "<path d='M21 12.22C21 6.73 16.74 3 12 3c-4.69 0-9 3.65-9 9.28-.6.34-1 .98-1 1.72v2c0 1.1.9 2 2 2h1v-6.1c0-3.87 3.13-7 7-7s7 3.13 7 7V19h-8v2h8c1.1 0 2-.9 2-2v-1.22c.59-.31 1-.92 1-1.64v-2.3c0-.7-.41-1.31-1-1.62'/><circle cx='9' cy='13' r='1'/><circle cx='15' cy='13' r='1'/><path d='M18 11.03C17.52 8.18 15.04 6 12.05 6c-3.03 0-6.29 2.51-6.03 6.45 2.47-1.01 4.33-3.21 4.86-5.89 1.31 2.63 4 4.44 7.12 4.47'/>";
const phoneCallbackSvg =
    "<path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02zm13.54-7.1-.71-.7L13 9.29V5h-1v6h6v-1h-4.15z'/>";
const queryStatsSvg =
    "<path d='M19.88 18.47c.44-.7.7-1.51.7-2.39 0-2.49-2.01-4.5-4.5-4.5s-4.5 2.01-4.5 4.5 2.01 4.5 4.49 4.5c.88 0 1.7-.26 2.39-.7L21.58 23 23 21.58zm-3.8.11c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5m-.36-8.5c-.74.02-1.45.18-2.1.45l-.55-.83-3.8 6.18-3.01-3.52-3.63 5.81L1 17l5-8 3 3.5L13 6zm2.59.5c-.64-.28-1.33-.45-2.05-.49L21.38 2 23 3.18z'/>";

const tileScale = 2.1;
const tileStep = 72;
const rowConfigs = [
    [supportAgentSvg, phoneCallbackSvg, queryStatsSvg],
    [phoneCallbackSvg, queryStatsSvg, supportAgentSvg],
    [queryStatsSvg, supportAgentSvg, phoneCallbackSvg]
];
const buildIconsPatternDataUri = (fillColor: string) => {
    const iconGroup = (iconSvg: string, x: number, y: number) =>
        `<g transform='translate(${x} ${y}) scale(${tileScale})' fill='${fillColor}'>${iconSvg}</g>`;
    const iconsPatternSvg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 216 216'>${rowConfigs
        .map((row, rowIndex) =>
            row
                .map((iconSvg, colIndex) => iconGroup(iconSvg, 10 + colIndex * tileStep, 11 + rowIndex * tileStep))
                .join('')
        )
        .join('')}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(iconsPatternSvg)}`;
};

const buildIconsPatternDataUriFromTheme = (theme: any) =>
    buildIconsPatternDataUri(theme.palette.layoutIconsPatternFillColor);

const useStyles = makeStyles((theme: any) => ({
    root: {
        position: 'relative',
        minHeight: '100vh',
        background: theme.palette.layoutBackground,
        overflow: 'hidden',
        '@media print': {
            display: 'none'
        }
    },
    pattern: {
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        backgroundImage: theme.palette.layoutPatternGradient,
        backgroundSize: '34px 34px'
    },
    iconsPattern: {
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundImage: `url("${buildIconsPatternDataUriFromTheme(theme)}")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '216px 216px',
        backgroundPosition: '-20px -20px'
    },
    content: {
        position: 'relative',
        zIndex: 1,
        minHeight: '100vh'
    }
}));

export const Layout = (props: React.ComponentProps<typeof Box>) => {
    const classes = useStyles();
    const theme = useAppTheme();
    const { children } = props;
    const easterTheme = getEasterTheme(theme.palette.isDarkTheme);

    const easterPatternStyle = easterTheme.enabled
        ? {
              backgroundImage: `url("${easterTheme.backgroundPattern.dataUri}")`,
              backgroundRepeat: 'repeat',
              backgroundSize: easterTheme.backgroundPattern.size,
              backgroundPosition: easterTheme.backgroundPattern.position
          }
        : undefined;

    return (
        <Box className={classes.root}>
            <div className={classes.pattern} />
            <div className={classes.iconsPattern} style={easterPatternStyle} />
            <Box className={classes.content}>{children}</Box>
            <Snow />
        </Box>
    );
};
